import { HttpClient } from "@angular/common/http";
import { Component, OnInit, Output, EventEmitter, Input } from "@angular/core";
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { AuthenticatorService } from '@aws-amplify/ui-angular';
import { Store, select } from "@ngrx/store";
import { SignUpInput } from "aws-amplify/auth";
import { parsePhoneNumber } from "libphonenumber-js/min";
import { NGXLogger } from "ngx-logger";
import { ToastrService } from 'ngx-toastr';
import { filter } from "rxjs/operators";
import { getSignUp } from "../../../../app/components/allusers/signup/signup.selector";
import { AmplifyAuthEvent } from "../../../../app/services/auth-service/amplify.actions";
import { isAmplifyConfirmSignUp } from "../../../../app/services/auth-service/amplify.selectors";
import { AuthService } from "../../../../app/services/auth-service/auth.service";
import { RouterService } from "../../../../app/services/route-service/router.service";
import { AgreementComponent } from "../agreement/agreement.component";
import {
  ConfirmSignUpAction,
  SignUpActionTypes,
  SignUpFailedAction,
  SignUpInProcessAction,
  SignUpUserInfoAction
} from "./signup.actions";
import { SignUpState } from "./signup.model";
import { emailToUsername } from "../../../utils/username.util";
import { PaymentsService } from "../../../services/payments.service";

interface Address {
  street_address: string;
  locality: string;
  region: string;
  postal_code: string;
  country: string;
}

/** US state codes used in License In State dropdown - for validating IP geolocation result */
const US_STATE_CODES = new Set(["AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ", "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"]);

/** Password must contain at least one digit, one lowercase, one uppercase, and 8+ characters */
const PASSWORD_PATTERN = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}$/;

/** Format up to 10 digits as (xxx) xxx-xxxx for display. */
function formatPhoneDisplay(value: string | null | undefined): string {
  if (value == null) return "";
  const digits = String(value).replace(/\D/g, "").slice(0, 10);
  if (digits.length === 0) return "";
  if (digits.length <= 3) return "(" + digits;
  if (digits.length <= 6) return "(" + digits.slice(0, 3) + ") " + digits.slice(3);
  return "(" + digits.slice(0, 3) + ") " + digits.slice(3, 6) + "-" + digits.slice(6);
}

/** Normalize US phone to 10 digits (extract digits from formatted input like (555) 111-2346). Returns null if invalid. */
function toUsTenDigits(value: string | null | undefined): string | null {
  if (value == null || String(value).trim() === "") return null;
  const digits = String(value).replace(/\D/g, "");
  if (digits.length < 10) return null;
  return digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits.length === 10 ? digits : null;
}

/** Validates phone number; accepts formatted input e.g. (555) 111-2346, 555-123-4567, 5551234567. Extracts digits and validates. */
function phoneNumberValidator(control: AbstractControl): ValidationErrors | null {
  const tenDigits = toUsTenDigits(control.value);
  if (tenDigits == null) return control.value != null && String(control.value).trim() !== "" ? { phoneNumber: true } : null;
  try {
    const parsed = parsePhoneNumber("+1" + tenDigits, "US");
    return parsed && parsed.isValid() ? null : { phoneNumber: true };
  } catch {
    return { phoneNumber: true };
  }
}

@Component({
  selector: "app-signup",
  templateUrl: "./signup.component.html",
  styleUrls: ["./signup.component.css"]
})
export class SignupComponent implements OnInit {
  signUpForm = new FormGroup({
    firstName: new FormControl("", Validators.required),
    lastName: new FormControl(""),
    phoneNumber: new FormControl("", [Validators.required, phoneNumberValidator]),
    email: new FormControl("", Validators.required),
    password: new FormControl("", [Validators.required, Validators.pattern(PASSWORD_PATTERN)]),
    rePassword: new FormControl("", [Validators.required, Validators.pattern(PASSWORD_PATTERN)]),
    licenseInState: new FormControl("", Validators.required),
    agreeToTerms: new FormControl(false, Validators.requiredTrue),
  });

  confirmSignUpForm = new FormGroup({
    username: new FormControl("", [Validators.required, Validators.pattern("[a-z0-9]+")]),
    verificationCode: new FormControl("", Validators.required)
  });

  currentState$ = this.signUpStore.pipe(select(getSignUp));
  currentState = SignUpActionTypes.SignUpUserInfo.toString();

  private isAmplifyConfirmSignup$ = this.amplifyStore.pipe(
    select(isAmplifyConfirmSignUp),
    filter(val => (val ? true : false))
  );
  errorMessage;
  successMessage;
  showPassword = false;
  /** True once the email has been recovered from the payment session and locked. */
  emailLocked = false;

  @Input() sessionParam: string | null = null;
  @Output() triggerSignin = new EventEmitter<void>();

  constructor(
    private authService: AuthService,
    private routeService: RouterService,
    private signUpStore: Store<SignUpState>,
    private amplifyStore: Store<AmplifyAuthEvent>,
    public dialog: MatDialog,
    public logger: NGXLogger,
    public authenticator: AuthenticatorService,
    private toastr: ToastrService,
    private http: HttpClient,
    private paymentsService: PaymentsService
  ) { }

  ngOnInit() {
    this.authenticator.route
    this.currentState$.subscribe(async signUpState => {
      if (signUpState.currentState) {
        this.currentState = signUpState.currentState.toString();
        if (signUpState.currentState === SignUpActionTypes.ConfirmSignUp) {
          if (
            signUpState &&
            signUpState.signUpOutput
          ) {
            this.successMessage =
              "Check for verification code at - " +
              signUpState.signUpOutput.nextStep["codeDeliveryDetails"].destination;
          }
          this.confirmSignUpForm.controls.username.setValue(
            signUpState.signUpInput.username
          );
        }
      } else {
        this.currentState = SignUpActionTypes.SignUpUserInfo.toString();
      }
    });

    this.isAmplifyConfirmSignup$.subscribe(authEvent => {
      this.signUpStore.dispatch(
        new ConfirmSignUpAction({
          currentState: SignUpActionTypes.ConfirmSignUp,
          signUpInput: authEvent.data
        } as SignUpState)
      );
    });

    this.setDefaultStateFromLocation();
    this.prefillFromPaymentSession();
  }

  /**
   * When the user arrives after paying (URL has ?session=<stripe checkout session id>),
   * recover the email they paid with and prefill it. The email is locked because the
   * account must use the same email to link to the existing paid record.
   */
  private async prefillFromPaymentSession(): Promise<void> {
    if (!this.sessionParam) {
      return;
    }
    try {
      const payment = await this.paymentsService.getPaymentBySessionId(this.sessionParam);
      if (!payment || !payment.email) {
        return;
      }
      this.signUpForm.patchValue({ email: payment.email });
      this.emailLocked = true;

      if (payment.firstName && !this.signUpForm.controls.firstName.value) {
        this.signUpForm.patchValue({ firstName: payment.firstName });
      }
      if (payment.lastName && !this.signUpForm.controls.lastName.value) {
        this.signUpForm.patchValue({ lastName: payment.lastName });
      }
      if (payment.licenseInstate) {
        this.signUpForm.patchValue({ licenseInState: payment.licenseInstate });
      }
    } catch (err) {
      // Webhook may not have recorded the payment yet, or session is invalid.
      // Leave the field editable and fall back to the "use your payment email" hint.
      this.logger.error(`Unable to recover email from payment session - ${JSON.stringify(err)}`);
    }
  }

  /**
   * Set License In State default from user's approximate location (IP-based geolocation).
   * Only sets if user appears to be in the US and we get a valid state code.
   * Uses ipapi.co (ip-api.com often returns 403 from browser).
   */
  private setDefaultStateFromLocation(): void {
    this.http.get<{ country_code: string; region_code: string }>("https://ipapi.co/json/")
      .subscribe({
        next: (res) => {
          const region = res?.region_code;
          if (res?.country_code === "US" && region && US_STATE_CODES.has(region)) {
            this.signUpForm.patchValue({ licenseInState: region });
          }
        },
        error: () => { /* ignore; leave dropdown at default empty */ }
      });
  }

  clearMessages() {
    this.errorMessage = null;
    this.successMessage = null;
  }

  gotoUserInfo() {
    this.signUpStore.dispatch(
      new SignUpUserInfoAction({
        currentState: SignUpActionTypes.SignUpUserInfo,
      } as SignUpState)
    );
  }

  openDialog(): void {
    const dialogRef = this.dialog.open(AgreementComponent, {
      width: "800px"
    });
  }

  /** On phone input: keep only digits and display as (xxx) xxx-xxxx. */
  formatPhoneInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const formatted = formatPhoneDisplay(input.value);
    if (formatted !== input.value) {
      this.signUpForm.controls.phoneNumber.setValue(formatted, { emitEvent: false });
    }
  }

  async signUp() {
    if (
      this.signUpForm.value.password === this.signUpForm.value.rePassword &&
      this.signUpForm.valid
    ) {
      this.clearMessages();

      // Username is hidden from the user and derived deterministically from
      // their email, so it is always the same for a given email address.
      const username = await emailToUsername(this.signUpForm.controls.email.value);

      const signUpParams = {
        username,
        password: this.signUpForm.controls.password.value,
        options: {
          userAttributes: {
            email: this.signUpForm.controls.email.value,
            given_name: this.signUpForm.controls.firstName.value,
            family_name: this.signUpForm.controls.lastName.value,
            phone_number: (() => {
              const ten = toUsTenDigits(this.signUpForm.controls.phoneNumber.value);
              return ten ? parsePhoneNumber("+1" + ten, "US").format("E.164") : "";
            })(),
            address: "N/A", // Cognito's `address` standard attribute is required and immutable on this pool; app doesn't collect it.
            "custom:cert_state": this.signUpForm.controls.licenseInState.value
          }
        }
      } as SignUpInput;

      this.signUpStore.dispatch(
        new SignUpInProcessAction({
          currentState: SignUpActionTypes.SignUpInProcess,
        } as SignUpState)
      );

      this.authService.signUp(signUpParams).then(
        success => {
          this.signUpStore.dispatch(
            new ConfirmSignUpAction({
              currentState: SignUpActionTypes.ConfirmSignUp,
            } as SignUpState)
          );
        },
        err => {
          this.logger.error(`User SignUp Failed - ${JSON.stringify(err)}`);
          this.errorMessage = err.message;
          if (err.code === "UserLambdaValidationException") {
            this.signUpStore.dispatch(
              new SignUpFailedAction({
                currentState: SignUpActionTypes.SignUpFailed,
                error: err.message
              } as SignUpState)
            );
            return;
          }
          this.signUpStore.dispatch(
            new SignUpFailedAction({
              currentState: SignUpActionTypes.SignUpFailed,
              error: err.message
            } as SignUpState)
          );
        }
      );
    } else {
      this.toastr.error("Sorry, Passwords do not match...");
    }
  }

  gotoHome() {
    this.routeService.gotoHome();
  }

  resendCode() {
    this.signUpStore.dispatch(
      new SignUpInProcessAction({
        currentState: SignUpActionTypes.SignUpInProcess
      } as SignUpState)
    );
    this.clearMessages();
    this.authService
      .resendVerificationCode(this.confirmSignUpForm.value.username)
      .then(success => {
        this.successMessage = `Verification code sent to Email - ${success.destination}`;
        this.signUpStore.dispatch(
          new ConfirmSignUpAction({
            currentState: SignUpActionTypes.ConfirmSignUp,
            signUpInput: { username: this.confirmSignUpForm.value.username }
          } as SignUpState)
        );
      })
      .catch(err => {
        this.errorMessage = err.message;
        this.signUpStore.dispatch(
          new ConfirmSignUpAction({
            currentState: SignUpActionTypes.ConfirmSignUp,
            signUpInput: { username: this.confirmSignUpForm.value.username }
          } as SignUpState)
        );
      });
  }

  async confirmSignUp() {
    this.signUpStore.dispatch(
      new SignUpInProcessAction({
        currentState: SignUpActionTypes.SignUpInProcess
      } as SignUpState)
    );
    this.clearMessages();
    try {
      await this.authService
        .confirmSignUp(
          this.confirmSignUpForm.value.username,
          this.confirmSignUpForm.value.verificationCode
        );
    } catch (error) {
      this.errorMessage = error.message;
    }
  }

}
